package com.karyadina.service

import com.karyadina.model.ExamType
import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.Flashcard
import com.karyadina.model.FlashcardStats
import com.karyadina.model.MasteryLevel
import com.karyadina.model.ReviewSession
import com.karyadina.model.SubjectStats
import com.karyadina.model.Unit as StudyUnit
import kotlinx.serialization.json.Json
import kotlinx.coroutines.*
import com.karyadina.util.currentTimeMillis
import kotlin.math.roundToInt

/**
 * Service for managing flashcards with spaced repetition
 */
interface FlashcardService {
    // Subject management
    fun createSubject(name: String, examType: ExamType, color: String): FlashCard_Subject
    fun getSubjects(): List<FlashCard_Subject>
    fun getSubject(id: String): FlashCard_Subject?
    fun updateSubject(subject: FlashCard_Subject)
    fun deleteSubject(id: String)
    
    // Unit management
    fun createUnit(subjectId: String, name: String, order: Int): StudyUnit
    fun getUnits(subjectId: String): List<StudyUnit>
    fun getUnit(id: String): StudyUnit?
    fun updateUnit(unit: StudyUnit)
    fun deleteUnit(id: String)
    
    // Flashcard management
    fun createCard(unitId: String, subjectId: String, question: String, answer: String, hint: String? = null, tags: List<String> = emptyList(), imagePaths: List<String> = emptyList(), hasScannedContent: Boolean = false): Flashcard
    fun createCardWithFormula(unitId: String, subjectId: String, question: String, answer: String, hint: String? = null, tags: List<String> = emptyList(), imagePaths: List<String> = emptyList(), formulaLatex: String? = null, formulaImagePath: String? = null, formulaDescription: String? = null, formulaVariables: Map<String, String> = emptyMap(), formulaType: String? = null): Flashcard
    fun getCards(unitId: String): List<Flashcard>
    fun getAllCards(subjectId: String): List<Flashcard>
    fun getCard(id: String): Flashcard?
    fun updateCard(card: Flashcard)
    fun deleteCard(id: String)
    fun toggleFavorite(cardId: String)
    
    // Review functionality
    fun getDueCards(subjectId: String? = null, unitId: String? = null): List<Flashcard>
    fun reviewCard(cardId: String, correct: Boolean)
    fun startReviewSession(subjectId: String, unitId: String? = null): ReviewSession
    fun endReviewSession(sessionId: String)
    
    // Statistics
    fun getStats(): FlashcardStats
    fun getSubjectStats(subjectId: String): SubjectStats
    fun getReviewHistory(): List<ReviewSession>
    
    /** Re-read all data from persistent storage (e.g. after cloud sync import) */
    fun refreshFromStorage()
}

/**
 * Implementation with spaced repetition algorithm
 */
class FlashcardServiceImpl(
    private val storage: FlashcardStorage = FlashcardStorageImpl()
) : FlashcardService {
    
    private val subjects = mutableListOf<FlashCard_Subject>()
    private val units = mutableListOf<StudyUnit>()
    private val cards = mutableListOf<Flashcard>()
    private val sessions = mutableListOf<ReviewSession>()
    private var currentSession: ReviewSession? = null
    
    init {
        // Load data from storage
        subjects.addAll(storage.loadSubjects())
        units.addAll(storage.loadUnits())
        cards.addAll(storage.loadFlashcards())
        sessions.addAll(storage.loadSessions())
        
        // Listen for storage refresh signals
        kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.Dispatchers.Default).launch {
            AppStorage.refreshNotifier.collect {
                refreshFromStorage()
            }
        }
    }

    override fun refreshFromStorage() {
        subjects.clear()
        units.clear()
        cards.clear()
        sessions.clear()
        subjects.addAll(storage.loadSubjects())
        units.addAll(storage.loadUnits())
        cards.addAll(storage.loadFlashcards())
        sessions.addAll(storage.loadSessions())
    }
    
    // Subject Management
    override fun createSubject(name: String, examType: ExamType, color: String): FlashCard_Subject {
        val now = currentTimeMillis()
        val subject = FlashCard_Subject(
            id = generateId("subject"),
            name = name,
            examType = examType.name,
            color = color,
            createdAt = now,
            updatedAt = now
        )
        subjects.add(subject)
        storage.saveSubjects(subjects)
        return subject
    }
    
    override fun getSubjects(): List<FlashCard_Subject> = subjects.sortedByDescending { it.createdAt }
    
    override fun getSubject(id: String): FlashCard_Subject? = subjects.find { it.id == id }
    
    override fun updateSubject(subject: FlashCard_Subject) {
        val index = subjects.indexOfFirst { it.id == subject.id }
        if (index != -1) {
            val updated = subject.copy(updatedAt = currentTimeMillis())
            subjects[index] = updated
            storage.saveSubjects(subjects)
        }
    }
    
    override fun deleteSubject(id: String) {
        subjects.removeAll { it.id == id }
        units.removeAll { it.subjectId == id }
        cards.removeAll { it.subjectId == id }
        storage.saveSubjects(subjects)
        storage.saveUnits(units)
        storage.saveFlashcards(cards)
    }
    
    // Unit Management
    override fun createUnit(subjectId: String, name: String, order: Int): StudyUnit {
        val now = currentTimeMillis()
        val unit = StudyUnit(
            id = generateId("unit"),
            subjectId = subjectId,
            name = name,
            order = order,
            createdAt = now,
            updatedAt = now
        )
        units.add(unit)
        storage.saveUnits(units)
        updateSubjectStats(subjectId)
        return unit
    }
    
    override fun getUnits(subjectId: String): List<StudyUnit> = 
        units.filter { it.subjectId == subjectId }.sortedBy { it.order }
    
    override fun getUnit(id: String): StudyUnit? = units.find { it.id == id }
    
    override fun updateUnit(unit: StudyUnit) {
        val index = units.indexOfFirst { it.id == unit.id }
        if (index != -1) {
            val updated = unit.copy(updatedAt = currentTimeMillis())
            units[index] = updated
            storage.saveUnits(units)
        }
    }
    
    override fun deleteUnit(id: String) {
        val unit = units.find { it.id == id } ?: return
        units.removeAll { it.id == id }
        cards.removeAll { it.unitId == id }
        storage.saveUnits(units)
        storage.saveFlashcards(cards)
        updateSubjectStats(unit.subjectId)
    }
    
    // Flashcard Management
    override fun createCard(
        unitId: String, 
        subjectId: String, 
        question: String, 
        answer: String, 
        hint: String?, 
        tags: List<String>,
        imagePaths: List<String>,
        hasScannedContent: Boolean
    ): Flashcard {
        val card = Flashcard(
            id = generateId("card"),
            unitId = unitId,
            subjectId = subjectId,
            question = question,
            answer = answer,
            hint = hint,
            tags = tags,
            imagePaths = imagePaths,
            hasScannedContent = hasScannedContent,
            nextReviewDate = currentTimeMillis() // Due immediately for new cards
        )
        cards.add(card)
        storage.saveFlashcards(cards)
        updateUnitStats(unitId)
        updateSubjectStats(subjectId)
        return card
    }
    
    override fun createCardWithFormula(
        unitId: String,
        subjectId: String,
        question: String,
        answer: String,
        hint: String?,
        tags: List<String>,
        imagePaths: List<String>,
        formulaLatex: String?,
        formulaImagePath: String?,
        formulaDescription: String?,
        formulaVariables: Map<String, String>,
        formulaType: String?
    ): Flashcard {
        val card = Flashcard(
            id = generateId("card"),
            unitId = unitId,
            subjectId = subjectId,
            question = question,
            answer = answer,
            hint = hint,
            tags = tags,
            imagePaths = imagePaths,
            hasScannedContent = imagePaths.isNotEmpty(),
            nextReviewDate = currentTimeMillis(),
            // Formula fields
            formulaLatex = formulaLatex,
            formulaImagePath = formulaImagePath,
            formulaDescription = formulaDescription,
            formulaVariables = formulaVariables,
            formulaType = formulaType,
            hasFormula = formulaLatex != null
        )
        cards.add(card)
        storage.saveFlashcards(cards)
        updateUnitStats(unitId)
        updateSubjectStats(subjectId)
        return card
    }
    
    override fun getCards(unitId: String): List<Flashcard> = 
        cards.filter { it.unitId == unitId }.sortedBy { it.createdAt }
    
    override fun getAllCards(subjectId: String): List<Flashcard> = 
        cards.filter { it.subjectId == subjectId }
    
    override fun getCard(id: String): Flashcard? = cards.find { it.id == id }
    
    override fun updateCard(card: Flashcard) {
        val index = cards.indexOfFirst { it.id == card.id }
        if (index != -1) {
            val updated = card.copy(updatedAt = currentTimeMillis())
            cards[index] = updated
            storage.saveFlashcards(cards)
        }
    }
    
    override fun deleteCard(id: String) {
        val card = cards.find { it.id == id } ?: return
        cards.removeAll { it.id == id }
        storage.saveFlashcards(cards)
        updateUnitStats(card.unitId)
        updateSubjectStats(card.subjectId)
    }
    
    override fun toggleFavorite(cardId: String) {
        val card = cards.find { it.id == cardId } ?: return
        val updated = card.copy(isFavorite = !card.isFavorite)
        updateCard(updated)
    }
    
    // Review Functionality with Spaced Repetition
    override fun getDueCards(subjectId: String?, unitId: String?): List<Flashcard> {
        val now = currentTimeMillis()
        return cards.filter { card ->
            val matchesSubject = subjectId == null || card.subjectId == subjectId
            val matchesUnit = unitId == null || card.unitId == unitId
            val isDue = card.nextReviewDate == null || card.nextReviewDate <= now
            val isNotMastered = card.masteryLevel != MasteryLevel.MASTERED.name // Fixes master card review
            matchesSubject && matchesUnit && isDue && isNotMastered
        }.sortedBy { it.nextReviewDate ?: 0 }
    }
    
    override fun reviewCard(cardId: String, correct: Boolean) {
        val card = cards.find { it.id == cardId } ?: return
        val currentMastery = try {
            MasteryLevel.valueOf(card.masteryLevel)
        } catch (e: Exception) {
            MasteryLevel.NEW
        }
        
        // Calculate new mastery level based on spaced repetition
        val newMastery = if (correct) {
            when (currentMastery) {
                MasteryLevel.NEW -> MasteryLevel.LEARNING
                MasteryLevel.LEARNING -> MasteryLevel.REVIEW
                MasteryLevel.REVIEW -> MasteryLevel.FAMILIAR
                MasteryLevel.FAMILIAR -> MasteryLevel.KNOWN
                MasteryLevel.KNOWN -> MasteryLevel.MASTERED
                MasteryLevel.MASTERED -> MasteryLevel.MASTERED
            }
        } else {
            // Demote on incorrect answer
            when (currentMastery) {
                MasteryLevel.NEW -> MasteryLevel.NEW
                MasteryLevel.LEARNING -> MasteryLevel.NEW
                MasteryLevel.REVIEW -> MasteryLevel.LEARNING
                MasteryLevel.FAMILIAR -> MasteryLevel.REVIEW
                MasteryLevel.KNOWN -> MasteryLevel.FAMILIAR
                MasteryLevel.MASTERED -> MasteryLevel.KNOWN
            }
        }
        
        // Calculate next review date
        val now = currentTimeMillis()
        val daysUntilNextReview = newMastery.reviewIntervalDays
        val nextReviewDate = now + (daysUntilNextReview * 24 * 60 * 60 * 1000)
        
        // Update card
        val updatedCard = card.copy(
            masteryLevel = newMastery.name,
            lastReviewedAt = now,
            nextReviewDate = nextReviewDate,
            reviewCount = card.reviewCount + 1,
            correctCount = if (correct) card.correctCount + 1 else card.correctCount,
            incorrectCount = if (!correct) card.incorrectCount + 1 else card.incorrectCount
        )
        
        updateCard(updatedCard)
        updateUnitStats(card.unitId)
        updateSubjectStats(card.subjectId)
        
        // Update current session
        currentSession?.let { session ->
            val updated = session.copy(
                cardsReviewed = session.cardsReviewed + 1,
                correctAnswers = if (correct) session.correctAnswers + 1 else session.correctAnswers,
                incorrectAnswers = if (!correct) session.incorrectAnswers + 1 else session.incorrectAnswers
            )
            currentSession = updated
        }
    }
    
    override fun startReviewSession(subjectId: String, unitId: String?): ReviewSession {
        val session = ReviewSession(
            id = generateId("session"),
            subjectId = subjectId,
            unitId = unitId,
            startTime = currentTimeMillis()
        )
        currentSession = session
        return session
    }
    
    override fun endReviewSession(sessionId: String) {
        currentSession?.let { session ->
            if (session.id == sessionId) {
                val completed = session.copy(
                    endTime = currentTimeMillis(),
                    completed = true
                )
                sessions.add(completed)
                storage.saveSessions(sessions)
                currentSession = null
            }
        }
    }
    
    // Statistics
    override fun getStats(): FlashcardStats {
        val totalCards = cards.size
        val dueCards = getDueCards().size
        val masteredCards = cards.count { 
            try {
                MasteryLevel.valueOf(it.masteryLevel) == MasteryLevel.MASTERED
            } catch (e: Exception) {
                false
            }
        }
        
        val completedSessions = sessions.filter { it.completed }
        val totalCorrect = completedSessions.sumOf { it.correctAnswers }
        val totalAnswered = completedSessions.sumOf { it.cardsReviewed }
        val avgAccuracy = if (totalAnswered > 0) (totalCorrect.toFloat() / totalAnswered) * 100 else 0f
        
        return FlashcardStats(
            totalSubjects = subjects.size,
            totalUnits = units.size,
            totalCards = totalCards,
            cardsToReview = dueCards,
            masteredCards = masteredCards,
            currentStreak = 0, // TODO: Implement streak tracking
            longestStreak = 0,
            totalReviewSessions = completedSessions.size,
            averageAccuracy = avgAccuracy
        )
    }
    
    override fun getSubjectStats(subjectId: String): SubjectStats {
        val subjectCards = cards.filter { it.subjectId == subjectId }
        val now = currentTimeMillis()
        
        val totalCards = subjectCards.size
        val newCards = subjectCards.count { it.masteryLevel == MasteryLevel.NEW.name }
        val learningCards = subjectCards.count { it.masteryLevel == MasteryLevel.LEARNING.name }
        val reviewCards = subjectCards.count { it.masteryLevel == MasteryLevel.REVIEW.name }
        val masteredCards = subjectCards.count { it.masteryLevel == MasteryLevel.MASTERED.name }
        val dueCards = subjectCards.count { 
            (it.nextReviewDate == null || it.nextReviewDate <= now) && it.masteryLevel != MasteryLevel.MASTERED.name
        }
        
        val totalCorrect = subjectCards.sumOf { it.correctCount }
        val totalReviewed = subjectCards.sumOf { it.reviewCount }
        val avgAccuracy = if (totalReviewed > 0) (totalCorrect.toFloat() / totalReviewed) * 100 else 0f
        
        val lastReview = subjectCards.mapNotNull { it.lastReviewedAt }.maxOrNull()
        
        return SubjectStats(
            subjectId = subjectId,
            totalCards = totalCards,
            newCards = newCards,
            learningCards = learningCards,
            reviewCards = reviewCards,
            masteredCards = masteredCards,
            dueForReview = dueCards,
            averageAccuracy = avgAccuracy,
            totalReviewTime = 0, // TODO: Calculate from sessions
            lastReviewDate = lastReview
        )
    }
    
    override fun getReviewHistory(): List<ReviewSession> = 
        sessions.filter { it.completed }.sortedByDescending { it.startTime }
    
    // Helper functions
    private fun updateUnitStats(unitId: String) {
        val unit = units.find { it.id == unitId } ?: return
        val unitCards = cards.filter { it.unitId == unitId }
        val masteredCards = unitCards.count { it.masteryLevel == MasteryLevel.MASTERED.name }
        
        val updated = unit.copy(
            totalCards = unitCards.size,
            masteredCards = masteredCards
        )
        updateUnit(updated)
    }
    
    private fun updateSubjectStats(subjectId: String) {
        val subject = subjects.find { it.id == subjectId } ?: return
        val subjectUnits = units.filter { it.subjectId == subjectId }
        val completedUnits = subjectUnits.count { unit ->
            val unitCards = cards.filter { it.unitId == unit.id }
            unitCards.isNotEmpty() && unitCards.all { it.masteryLevel == MasteryLevel.MASTERED.name }
        }
        
        val updated = subject.copy(
            totalUnits = subjectUnits.size,
            completedUnits = completedUnits
        )
        updateSubject(updated)
    }
    
    private fun generateId(prefix: String): String {
        return "${prefix}_${currentTimeMillis()}_${(1000..9999).random()}"
    }
}
